import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Home, User, LogIn, LogOut, ShieldCheck, UserPlus, Store, ClipboardList, Sun, Moon, X, Bell, Building2 } from "lucide-react";
import { usePlayer } from "@/lib/asv";
import { useAuth, isAdmin } from "@/context/AuthContext";

const Item = ({ to, icon: Icon, label, onClick, testid, extra }) => {
  const cls = "flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold hover:bg-black/5";
  const body = <><Icon className="h-5 w-5 asv-muted" /><span className="flex-1 text-left">{label}</span>{extra}</>;
  return to ? <Link to={to} onClick={onClick} className={cls} data-testid={testid}>{body}</Link>
    : <button type="button" onClick={onClick} className={cls} data-testid={testid}>{body}</button>;
};

export function MobileMenu({ open, onClose, dark, setDark }) {
  const { player, unread, logout } = usePlayer();
  const { user, logout: staffLogout } = useAuth();
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 md:hidden" data-testid="asv-mobile-menu">
      <button type="button" aria-label="Close menu" className="absolute inset-0 bg-black/50" onClick={onClose} data-testid="asv-menu-backdrop" />
      <div className="asv-rise absolute inset-x-0 bottom-0 rounded-t-[1.75rem] border-t asv-line p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]" style={{ background: "var(--asv-surface)" }}>
        <div className="mb-2 flex items-center justify-between px-2">
          <p className="asv-display text-lg font-extrabold">{player ? `Hi, ${player.name.split(" ")[0]}` : "Menu"}</p>
          <button type="button" onClick={onClose} aria-label="Close" className="asv-btn-ghost !p-2" data-testid="asv-menu-close"><X className="h-4 w-4" /></button>
        </div>
        <Item to="/" icon={Home} label="Home" onClick={onClose} testid="asv-menu-home" />
        <Item to="/sport-centres" icon={Building2} label="Sport Centres" onClick={onClose} testid="asv-menu-centres" />
        {player ? <Item to="/account" icon={User} label="Account" onClick={onClose} testid="asv-menu-account" extra={!!unread && <Bell className="h-4 w-4 asv-accent" />} />
          : isAdmin(user) ? <Item to="/platform" icon={ShieldCheck} label="Platform admin" onClick={onClose} testid="asv-menu-admin" />
          : <Item to="/signin" icon={LogIn} label="Sign in or create account" onClick={onClose} testid="asv-menu-signin" />}
        <div className="my-2 border-t asv-line" />
        <Item to="/join/user" icon={UserPlus} label="Join as a player" onClick={onClose} testid="asv-menu-join-user" />
        <Item to="/join/vendor" icon={Store} label="List your venue" onClick={onClose} testid="asv-menu-join-vendor" />
        <Item to="/register-venue" icon={ClipboardList} label="Register your venue" onClick={onClose} testid="asv-menu-register-venue" />
        <Item to="/contact" icon={Mail} label="Contact us" onClick={onClose} testid="asv-menu-contact" />
        <div className="my-2 border-t asv-line" />
        <Item icon={dark ? Sun : Moon} label={dark ? "Light mode" : "Dark mode"} onClick={() => setDark(!dark)} testid="asv-menu-theme" />
        {player ? <Item icon={LogOut} label="Sign out" onClick={() => { logout(); onClose(); }} testid="asv-menu-signout" />
          : isAdmin(user) && <Item icon={LogOut} label="Sign out" onClick={() => { staffLogout(); onClose(); }} testid="asv-menu-admin-signout" />}
      </div>
    </div>
  );
}
